import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';

const LOGO_URL = '/assets/mindvrik-logo-transparent.png';

const navLinks = [
  { label: 'About', href: '#about' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Services', href: '#plans' },
  { label: 'Reports', href: '#reports' },
  { label: 'Our Team', href: '/team' },
  { label: 'Our Partners', href: '#partners' },
  { label: 'Contact', href: '#contact' },
];

const Navbar = ({ onOpenCareerLibrary, onOpenAssessment, onOpenParentDashboard, onOpenAuth, onNavigateSection, user }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNav = (href) => {
    setMenuOpen(false);
    if (onNavigateSection) {
      onNavigateSection(href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleLink = (link) => {
    if (link.action === 'careers') {
      setMenuOpen(false);
      onOpenCareerLibrary?.();
      return;
    }
    handleNav(link.href);
  };

  const handleAccountAction = () => {
    setMenuOpen(false);
    if (user?.role === 'admin') {
      onOpenAuth?.();
      return;
    }
    if (user) {
      onOpenParentDashboard?.();
      return;
    }
    onOpenAuth?.();
  };

  const accountLabel = user?.role === 'admin' ? 'Sign In' : user ? 'Dashboard' : 'Sign In';

  return (
    <nav
      data-testid="navbar"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        transition: 'all 0.35s ease',
        background: scrolled ? 'rgba(249,249,247,0.88)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(42,157,143,0.1)' : '1px solid transparent',
        boxShadow: scrolled ? '0 4px 24px rgba(0,0,0,0.04)' : 'none',
      }}
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '108px' }}>
          {/* Logo */}
          <a
            href="#hero"
            onClick={(e) => { e.preventDefault(); handleNav('#hero'); }}
            data-testid="nav-logo"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '150px',
              height: '108px',
              textDecoration: 'none',
              flexShrink: 0,
            }}
          >
            <img
              src={LOGO_URL}
              alt="MindVrik"
              style={{
                width: '142px',
                height: '86px',
                objectFit: 'contain',
                objectPosition: 'center',
                display: 'block',
              }}
            />
          </a>

          {/* Desktop Nav Links */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '18px', marginLeft: '24px' }} className="nav-links hidden md:flex">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href || '#careers'}
                className="nav-link"
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                onClick={(e) => { e.preventDefault(); handleLink(link); }}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop CTA */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }} className="hidden md:flex">
            <button
              className="btn-ghost"
              style={{ padding: '9px 22px', fontSize: '13px' }}
              data-testid="nav-login-btn"
              onClick={handleAccountAction}
            >
              {accountLabel}
            </button>
            <button
              className="btn-primary"
              style={{ padding: '10px 24px', fontSize: '13px' }}
              data-testid="nav-start-btn"
              onClick={() => onOpenAssessment?.()}
            >
              Start Assessment
            </button>
          </div>

          {/* Mobile menu toggle */}
          <button
            className="flex md:hidden"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="mobile-menu-btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '44px',
              height: '44px',
              borderRadius: '14px',
              background: 'rgba(255,255,255,0.75)',
              border: '1px solid rgba(42,157,143,0.12)',
              cursor: 'pointer',
              color: '#1A202C',
              padding: '8px',
              flexShrink: 0,
            }}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        style={{
          overflow: 'hidden',
          maxHeight: menuOpen ? '400px' : '0',
          transition: 'max-height 0.35s ease',
          background: 'rgba(249,249,247,0.97)',
          backdropFilter: 'blur(20px)',
          borderTop: menuOpen ? '1px solid rgba(42,157,143,0.1)' : 'none',
        }}
        data-testid="mobile-menu"
      >
        <div style={{ padding: '16px 24px 24px' }}>
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href || '#careers'}
              style={{
                display: 'block',
                padding: '12px 0',
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 500,
                fontSize: '15px',
                color: '#1A202C',
                textDecoration: 'none',
                borderBottom: '1px solid rgba(0,0,0,0.05)',
              }}
              onClick={(e) => { e.preventDefault(); handleLink(link); }}
            >
              {link.label}
            </a>
          ))}
          <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button className="btn-ghost" style={{ width: '100%', justifyContent: 'center' }} data-testid="mobile-signin-btn" onClick={handleAccountAction}>{accountLabel}</button>
            <button className="btn-primary" style={{ width: '100%', justifyContent: 'center' }} data-testid="mobile-start-btn" onClick={() => onOpenAssessment?.()}>Start Assessment</button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
